import {
  type ComponentProps,
  type FC, useCallback,
} from 'react';
import { useDispatch } from 'react-redux';
import { CONFIG } from '~/_shared/constants/config';
import { defaultBoundaryFillActivated } from '~/sidebar/sidebarApps/mapTools/boundary/extraFills/boundaryExtraFills.actionCreators';
import { useExtraFillConfigSelector } from '~/sidebar/sidebarApps/mapTools/boundary/extraFills/boundaryExtraFillSelectors';
import { getExtraActionsForBaseFill } from '~/sidebar/sidebarApps/mapTools/boundary/extraFills/fillTitleExtraActions';
import { useGetBoundaryFillEntities } from '~/sidebar/sidebarApps/mapTools/boundary/hooks/useGetBoundaryFillEntities';
import {
  checkBoundaryIsCustom, useBoundaryGroupsSelector,
} from '~/store/boundaryGroups/boundaryGroups.selectors';
import { type BoundaryTerritoryType } from '~/store/boundaryTerritoryGroups/boundaryTerritoryGroup.type';
import { useBoundaryTerritoryGroupsSelector } from '~/store/boundaryTerritoryGroups/boundaryTerritoryGroups.selectors';
import {
  ActiveBoundaryFillInfoComponent, type ActiveBoundaryFillInfoEntities,
} from './activeBoundaryFillInfo.component';

const noFillEntities: ComponentProps<typeof ActiveBoundaryFillInfoComponent>['fillEntities'] = {
  mainEntity: 'Manual fill',
};

type ActiveBoundaryFillInfoProps = Readonly<{
  boundaryTerritoryType: BoundaryTerritoryType;
  boundaryTerritoryGroupId: number;
}>;

export const ActiveBoundaryFillInfoContainer: FC<ActiveBoundaryFillInfoProps> = ({
  boundaryTerritoryGroupId,
  boundaryTerritoryType,
}) => {
  const dispatch = useDispatch();
  const boundaryTerritoryGroups = useBoundaryTerritoryGroupsSelector();
  const boundaryGroups = useBoundaryGroupsSelector();
  const boundaryTerritoryGroup = boundaryTerritoryGroups.find(
    group => group.boundaryTerritoryGroupId === boundaryTerritoryGroupId,
  );
  const btgExtraFills = useExtraFillConfigSelector(boundaryTerritoryGroup?.boundaryTerritoryGroupId);
  const extraFillCount = btgExtraFills?.extraFills?.length ?? 0;
  const boundaryGroup = boundaryGroups.find(group => group.id === boundaryTerritoryGroup?.boundaryGroupId);

  const entities: ActiveBoundaryFillInfoEntities | null = useGetBoundaryFillEntities({
    columnId: boundaryTerritoryGroup?.settings?.bucketColumnId,
    boundaryTerritoryGroup,
    calculateBucketFunction: boundaryTerritoryGroup?.settings.calculateBucketFunction,
    boundaryTerritoryType,
  });

  const activateFill = useCallback(() => {
    dispatch(defaultBoundaryFillActivated({ boundaryTerritoryGroupId }));
  }, [boundaryTerritoryGroupId, dispatch]);

  const extraActions = CONFIG.ENABLE_ALT_BOUNDARY_FILLS && boundaryTerritoryGroup && !checkBoundaryIsCustom(boundaryGroup) ?
    getExtraActionsForBaseFill({ boundaryTerritoryGroupId, boundaryGroupId: boundaryTerritoryGroup.boundaryGroupId }) :
    undefined;

  return (entities || (CONFIG.ENABLE_ALT_BOUNDARY_FILLS && (extraFillCount > 0)) ? (
    <ActiveBoundaryFillInfoComponent
      fillEntities={entities ?? noFillEntities}
      extraActions={extraActions}
      onClick={CONFIG.ENABLE_ALT_BOUNDARY_FILLS ? activateFill : undefined}
    />
  ) : null);
};
